import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, CheckCircle2, AlertCircle, AlertTriangle, Copy, FileText } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { DjayNarrator } from '../../../mascot';
import { type Classroom, sanitizeDisplayName } from '../../../accounts';
interface BulkImportProps {
  teacherId: string;
  onComplete: () => void;
  onCancel: () => void;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
}
interface ParsedStudent {
  name: string;
  grade: number;
  status: 'pending' | 'success' | 'error';
  accountId?: string;
  linkCode?: string;
  errorMsg?: string;
  warnings: string[];
  skip?: boolean;
}
export function BulkImport({
  teacherId,
  onComplete,
  onCancel,
  classrooms = [],
  selectedClassroomId = null
}: BulkImportProps) {
  const {
    createAndLinkStudent,
    accounts
  } = useAppStore();
  const defaultClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const [inputText, setInputText] = useState('');
  const [defaultGrade, setDefaultGrade] = useState(defaultClassroom ? defaultClassroom.groupLevel.toString() : '6');
  const [targetClassroomId, setTargetClassroomId] = useState<string>(selectedClassroomId || '');
  const [parsedStudents, setParsedStudents] = useState<ParsedStudent[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<'input' | 'preview' | 'result'>('input');
  const [copied, setCopied] = useState(false);
  /** Get all linked learners for duplicate detection */
  const getExistingLearners = (): Array<{
    name: string;
    grade: number;
  }> => {
    if (!accounts?.relationships?.teacherToLearners?.[teacherId]) return [];
    return accounts.relationships.teacherToLearners[teacherId].map(id => {
      const account = accounts.accountsById[id];
      if (account && account.role === 'learner') {
        return {
          name: account.displayName?.toLowerCase().trim() || '',
          grade: account.grade || 0
        };
      }
      return null;
    }).filter((l): l is {
      name: string;
      grade: number;
    } => l !== null);
  };
  const handleParse = () => {
    if (!inputText.trim()) return;
    const lines = inputText.split('\n').filter(line => line.trim().length > 0);
    const targetClassroom = classrooms.find(c => c.id === targetClassroomId);
    const existingLearners = getExistingLearners();
    const seenNames = new Set<string>();
    const students: ParsedStudent[] = lines.map(line => {
      const parts = line.split(',').map(p => p.trim());
      const rawName = parts[0];
      const name = rawName ? sanitizeDisplayName(rawName.trim()) : '';
      let grade = parseInt(defaultGrade, 10);
      if (parts.length > 1) {
        const parsedGrade = parseInt(parts[1], 10);
        if (!isNaN(parsedGrade) && parsedGrade >= 1 && parsedGrade <= 8) {
          grade = parsedGrade;
        }
      }
      const warnings: string[] = [];
      let hasError = false;
      // Validate: empty name
      if (!name || name.trim().length === 0) {
        warnings.push('Naam is leeg');
        hasError = true;
      }
      // Validate: name too short
      if (name && name.length > 0 && name.length < 2) {
        warnings.push('Naam moet minimaal 2 tekens zijn');
        hasError = true;
      }
      // Validate: name too long
      if (name.length > 100) {
        warnings.push('Naam mag maximaal 100 tekens zijn');
        hasError = true;
      }
      // Validate: grade mismatch with target classroom
      if (targetClassroom && grade !== targetClassroom.groupLevel) {
        warnings.push(`Groep ${grade} komt niet overeen met klas "${targetClassroom.name}" (groep ${targetClassroom.groupLevel})`);
      }
      // Validate: duplicate with existing learners
      const normalizedName = name?.toLowerCase().trim() || '';
      if (normalizedName) {
        const isDuplicate = existingLearners.some(l => l.name === normalizedName && l.grade === grade);
        if (isDuplicate) {
          warnings.push(`"${name}" (groep ${grade}) is al gekoppeld aan je account`);
        }
      }
      // Validate: duplicate within import list
      if (normalizedName && seenNames.has(normalizedName)) {
        warnings.push(`"${name}" komt meerdere keren voor in de lijst`);
      }
      seenNames.add(normalizedName);
      return {
        name,
        grade,
        status: hasError ? 'error' : 'pending',
        warnings,
        skip: false
      };
    });
    setParsedStudents(students);
    setStep('preview');
  };
  const handleImport = async () => {
    setIsProcessing(true);
    const results = [...parsedStudents];
    for (let i = 0; i < results.length; i++) {
      const student = results[i];
      if (student.status === 'error' || student.skip) continue; // Skip invalid or opted-out entries
      try {
        // F4: Atomic operation — create account, link to teacher, generate code in one state update
        const {
          studentId,
          linkCode
        } = createAndLinkStudent(teacherId, {
          displayName: student.name,
          grade: student.grade
        }, targetClassroomId || undefined);
        // Leeg resultaat = geweigerd. `createAndLinkStudent` maakt geen
        // accounts meer aan zolang identiteit via Supabase loopt, want die
        // leerlingen zouden alleen lokaal bestaan: geen authrij, geen profiel,
        // geen toestemmingsstatus, geen bewaartermijn. Zonder deze controle
        // meldde een import van dertig leerlingen dertig keer "gelukt" en gaf
        // dertig lege koppelcodes terug.
        if (!studentId || !linkCode) {
          results[i] = {
            ...student,
            status: 'error',
            errorMsg: 'Aanmaken via import kan nog niet'
          };
          continue;
        }
        results[i] = {
          ...student,
          status: 'success',
          accountId: studentId,
          linkCode
        };
      } catch (err) {
        results[i] = {
          ...student,
          status: 'error',
          errorMsg: 'Fout bij aanmaken'
        };
      }
    }
    setParsedStudents(results);
    setIsProcessing(false);
    setStep('result');
  };
  const copyAllCodes = () => {
    const text = parsedStudents.filter(s => s.status === 'success' && s.linkCode).map(s => `${s.name}: ${s.linkCode}`).join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const totalWarnings = parsedStudents.reduce((sum, s) => sum + s.warnings.length, 0);
  return <div className="space-y-4" data-id="element-9383" data-mp="69ks4p77hhde">
      {step === 'input' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-4" data-id="element-9384" data-mp="1mow2giu05d0q">
        
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm text-slate-600" data-id="element-9385" data-mp="1hp9f3gkgmge7">
            <p className="font-semibold text-slate-800 mb-1" data-id="element-9386" data-mp="2zmwd7zpubujx">Hoe werkt het?</p>
            <p data-id="element-9387" data-mp="34nk1idmopxag">
              Plak een lijst met namen (één per regel). Je kunt optioneel de
              groep toevoegen met een komma (bijv. "Emma de Vries, 7").
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-9388" data-mp="2vlu8j3d5mqak">
            <Select variant="admin" label="Standaard groep (als niet opgegeven)" value={defaultGrade} onChange={e => setDefaultGrade(e.target.value)} options={[{
          value: '6',
          label: 'Groep 6'
        }, {
          value: '7',
          label: 'Groep 7'
        }, {
          value: '8',
          label: 'Groep 8'
        }]} data-id="element-9389" data-mp="2vo6yo3t2g6yp" />
          

            {classrooms.length > 0 && <Select variant="admin" label="Toevoegen aan klas" value={targetClassroomId} onChange={e => {
          const newId = e.target.value;
          setTargetClassroomId(newId);
          if (newId) {
            const cls = classrooms.find(c => c.id === newId);
            if (cls) setDefaultGrade(cls.groupLevel.toString());
          }
        }} placeholder="Geen klas" options={classrooms.map(c => ({
          value: c.id,
          label: c.name
        }))} data-id="element-9390" data-mp="3ej3t4nu7u9te" />}
          </div>

          <div data-id="element-9391" data-mp="1x9q0ovw79iux">
            <label className="block text-sm font-medium text-slate-700 mb-1 flex justify-between" data-id="element-9392" data-mp="1x73t4w9b73cp">
              <span data-id="element-9393" data-mp="1s8itoz1vsnpb">Namenlijst</span>
              <span className="text-slate-400 font-normal" data-id="element-9394" data-mp="2pydtdcja3aii">
                Eén leerling per regel
              </span>
            </label>
            <textarea value={inputText} onChange={e => setInputText(e.target.value)} rows={8} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm" placeholder={'Emma de Vries\nNoah Jansen, 7\nSophie Bakker'} data-id="element-9395" data-mp="2kzu2qytqpajt" />
          
          </div>

          <div className="pt-2 flex justify-end gap-2" data-id="element-9396" data-mp="10j7allub9xyu">
            <Button variant="secondary" onClick={onCancel} data-id="element-9397" data-mp="10ie9b0e4g0o3">
              Annuleren
            </Button>
            <Button onClick={handleParse} disabled={!inputText.trim()} data-id="element-9398" data-mp="3c843emwlhv3e">
              <FileText className="w-4 h-4 mr-2" data-id="element-9399" data-mp="3g7crpc8v90rk" />
              Namen controleren
            </Button>
          </div>
        </motion.div>}

      {step === 'preview' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-4" data-id="element-9400" data-mp="3pxxqazoji5ah">
        
          <div className="flex items-center justify-between mb-2" data-id="element-9401" data-mp="3uxk53dk0ntle">
            <h4 className="font-bold text-slate-800" data-id="element-9402" data-mp="3uzdayqmt29sc">
              Controleer de lijst ({parsedStudents.length} leerlingen)
            </h4>
            <button onClick={() => setStep('input')} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-9403" data-mp="7s2xsrf4r1uv">
            
              Lijst aanpassen
            </button>
          </div>

          {/* Validation warnings banner */}
          {totalWarnings > 0 && <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 flex items-start gap-3" data-id="element-9404" data-mp="2eisixidgrikj">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-9405" data-mp="11hp6h88jli91" />
              <div data-id="element-9406" data-mp="16gjyn0segj5t">
                <h4 className="font-bold text-amber-900 text-sm" data-id="element-9407" data-mp="16g0gbiyu7sr9">
                  {totalWarnings} waarschuwing{totalWarnings !== 1 ? 'en' : ''}{' '}
                  gevonden
                </h4>
                <p className="text-xs text-amber-700 mt-0.5" data-id="element-9408" data-mp="1gf96s1uhup8v">
                  Je kunt toch importeren, maar controleer de gemarkeerde rijen.
                </p>
              </div>
            </div>}

          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg" data-id="element-9409" data-mp="1pew6mj7krmzt">
            <table className="w-full text-sm text-left" data-id="element-9410" data-mp="1u6eb3h8t45pj">
              <thead className="bg-slate-50 text-slate-600 sticky top-0" data-id="element-9411" data-mp="1u6wdkvm2bg5w">
                <tr data-id="element-9412" data-mp="1z4630b9gbrtq">
                  <th className="px-4 py-2 font-medium w-10" data-id="element-9413" data-mp="1z4fr1taxrows">
                    <input type="checkbox" checked={parsedStudents.every(s => !s.skip)} onChange={e => {
                  const checked = e.target.checked;
                  setParsedStudents(parsedStudents.map(s => ({
                    ...s,
                    skip: !checked
                  })));
                }} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600" data-id="element-9414" data-mp="2453j8iawibsl" />
                  
                  </th>
                  <th className="px-4 py-2 font-medium" data-id="element-9415" data-mp="3jni48sadr4mn">Naam</th>
                  <th className="px-4 py-2 font-medium w-24" data-id="element-9416" data-mp="3ep6vylzqh26j">Groep</th>
                  <th className="px-4 py-2 font-medium w-10" data-id="element-9417" data-mp="g69r2lqlksy9"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-9418" data-mp="b6wv9bhr0bjj">
                {parsedStudents.map((student, idx) => <tr key={idx} className={`hover:bg-slate-50 ${student.skip ? 'opacity-50' : ''} ${student.status === 'error' ? 'bg-red-50/50' : student.warnings.length > 0 ? 'bg-amber-50/50' : ''}`} data-id="element-9419" data-mp="6g6mrpe0zskm" data-mpi={idx}>
                
                    <td className="px-4 py-2" data-id="element-9420" data-mp="yy2iibq0q63v">
                      <input type="checkbox" checked={!student.skip} onChange={e => {
                  const newStudents = [...parsedStudents];
                  newStudents[idx] = {
                    ...student,
                    skip: !e.target.checked
                  };
                  setParsedStudents(newStudents);
                }} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600" data-id="element-9421" data-mp="yyu732l773kn" />
                  
                    </td>
                    <td className={`px-4 py-2 font-medium ${student.status === 'error' ? 'text-red-800' : 'text-slate-800'}`} data-id="element-9422" data-mp="2jlv93xttbaev">
                  
                      {student.name || '(Leeg)'}
                    </td>
                    <td className={`px-4 py-2 ${student.warnings.some(w => w.includes('Groep')) ? 'text-amber-700 font-bold' : 'text-slate-600'}`} data-id="element-9423" data-mp="29n5ovyeg1dvg">
                  
                      Groep {student.grade}
                    </td>
                    <td className="px-4 py-2" data-id="element-9424" data-mp="3q2yvuprtdpr8">
                      {student.warnings.length > 0 && <span title={student.warnings.join('\n')} className="cursor-help" data-id="element-9425" data-mp="3v2l5kp8fonva">
                    
                          {student.status === 'error' ? <AlertCircle className="w-4 h-4 text-red-500" data-id="element-9426" data-mp="9270rsps84ik" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-9427" data-mp="4217y97s0byw" />}
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Per-student warning details */}
          {totalWarnings > 0 && <div className="space-y-1" data-id="element-9428" data-mp="d1ftqvczzyd2">
              {parsedStudents.filter(s => s.warnings.length > 0).map((s, idx) => <div key={idx} className={`text-xs px-3 py-1.5 rounded-lg border ${s.status === 'error' ? 'text-red-700 bg-red-50 border-red-100' : 'text-amber-700 bg-amber-50 border-amber-100'}`} data-id="element-9429" data-mp="rtq2zxbzfejj" data-mpi={idx}>
            
                    <span className="font-bold" data-id="element-9430" data-mp="wqqc6h44oxev">{s.name || '(leeg)'}:</span>{' '}
                    {s.warnings.join('; ')}
                  </div>)}
            </div>}

          <div className="pt-2 flex justify-end gap-2" data-id="element-9431" data-mp="32k6qlp97fztk">
            <Button variant="secondary" onClick={onCancel} disabled={isProcessing} data-id="element-9432" data-mp="2yil7hm62upax">
            
              Annuleren
            </Button>
            <Button onClick={handleImport} disabled={isProcessing || parsedStudents.filter(s => s.status !== 'error' && !s.skip).length === 0} data-id="element-9433" data-mp="2zyqrb054hfa2">
            
              {isProcessing ? <>Bezig met importeren...</> : <>
                  <Upload className="w-4 h-4 mr-2" data-id="element-9434" data-mp="38y40rst8y3us" />
                  Importeer{' '}
                  {parsedStudents.filter(s => s.status !== 'error' && !s.skip).length}{' '}
                  leerlingen
                </>}
            </Button>
          </div>
        </motion.div>}

      {step === 'result' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-4" data-id="element-9435" data-mp="2l7zufdoobq03">
        
          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 flex items-start gap-3" data-id="element-9436" data-mp="2v0p5i7gbb1uw">
            <div className="flex-shrink-0 mt-0.5" data-id="element-9437" data-mp="1xlb7ueopz9d5">
              <DjayNarrator size="sm" emotion="celebrating" data-id="element-9438" data-mp="1xip4kr8v3g0q" />
            </div>
            <div data-id="element-9439" data-mp="22ia2cycapibv">
              <h4 className="font-bold text-emerald-900" data-id="element-9440" data-mp="22hrypgnb01tx">Import voltooid!</h4>
              <p className="text-sm text-emerald-700" data-id="element-9441" data-mp="1uztr5hoon0tc">
                Er zijn{' '}
                {parsedStudents.filter(s => s.status === 'success').length}{' '}
                leerlingen toegevoegd
                {targetClassroomId ? ' aan je klas' : ' aan je account'}. Deel
                onderstaande koppelcodes met de leerlingen.
              </p>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg" data-id="element-9442" data-mp="1c29ct4jjnkmz">
            <table className="w-full text-sm text-left" data-id="element-9443" data-mp="179pzm8orqrip">
              <thead className="bg-slate-50 text-slate-600 sticky top-0" data-id="element-9444" data-mp="12bo7ky9d7hv3">
                <tr data-id="element-9445" data-mp="129licgg1dsrf">
                  <th className="px-4 py-2 font-medium" data-id="element-9446" data-mp="xcld27r0gu1d">Naam</th>
                  <th className="px-4 py-2 font-medium w-32" data-id="element-9447" data-mp="xe643ne49uuh">Koppelcode</th>
                  <th className="px-4 py-2 font-medium w-24" data-id="element-9448" data-mp="t9yg5sfhgixb">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-9449" data-mp="ocf2atmp685w">
                {parsedStudents.map((student, idx) => <tr key={idx} className="hover:bg-slate-50" data-id="element-9450" data-mp="empyq9vkzhcw" data-mpi={idx}>
                    <td className="px-4 py-2 font-medium text-slate-800" data-id="element-9451" data-mp="iqe88y3rwn0s">
                      {student.name}
                    </td>
                    <td className="px-4 py-2" data-id="element-9452" data-mp="4ro97cxe8d9s">
                      {student.linkCode ? <span className="font-mono font-bold text-indigo-700 tracking-wider bg-indigo-50 px-2 py-1 rounded" data-id="element-9453" data-mp="9oepa09778v7">
                          {student.linkCode}
                        </span> : '-'}
                    </td>
                    <td className="px-4 py-2" data-id="element-9454" data-mp="12dc7y4hbn7oh">
                      {student.status === 'success' ? <span className="text-emerald-600 flex items-center gap-1 text-xs font-medium" data-id="element-9455" data-mp="nnxxsu5x8m81">
                          <CheckCircle2 className="w-3 h-3" data-id="element-9456" data-mp="sn0tho00hg3c" /> Gelukt
                        </span> : <span className="text-red-600 flex items-center gap-1 text-xs font-medium" title={student.errorMsg} data-id="element-9457" data-mp="1gd4tlldzqxpl">
                    
                          <AlertCircle className="w-3 h-3" data-id="element-9458" data-mp="1l5o94nw8o2x1" /> Fout
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="pt-2 flex justify-between items-center" data-id="element-9459" data-mp="30vfcrdzhknra">
            <Button variant="secondary" onClick={copyAllCodes} className="bg-white" data-id="element-9460" data-mp="3em0wt60of3qr">
            
              {copied ? <>
                  <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" data-id="element-9461" data-mp="3amaapfm3hbsk" />{' '}
                  Gekopieerd!
                </> : <>
                  <Copy className="w-4 h-4 mr-2" data-id="element-9462" data-mp="2hx321nsxf5tl" /> Kopieer alle codes
                </>}
            </Button>
            <Button onClick={onComplete} data-id="element-9463" data-mp="2vwcf4elu5jtc">Klaar</Button>
          </div>
        </motion.div>}
    </div>;
}